import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  clearAccessToken,
  fetchCurrentUser,
  isUnauthorized,
  readAccessToken,
  requestAccessToken,
  storeAccessToken,
} from '../lib/authSession';
import type { CurrentUser } from '../lib/authSession';

/**
 * `loading`       — a token exists and the profile is being resolved
 * `authenticated` — profile resolved, `user` is populated
 * `anonymous`     — no token, or the token was rejected
 * `error`         — a token exists but the profile could not be loaded
 *                   (backend unreachable); the session is NOT discarded
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface AuthContextValue {
  user: CurrentUser | null;
  status: AuthStatus;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const PROFILE_UNAVAILABLE =
  'Не удалось загрузить профиль пользователя. Проверьте доступность сервера.';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>(() =>
    readAccessToken() ? 'loading' : 'anonymous',
  );
  const [error, setError] = useState<string | null>(null);

  const adoptSession = useCallback(async () => {
    const account = await fetchCurrentUser();
    setUser(account);
    setError(null);
    setStatus('authenticated');
  }, []);

  const discardSession = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setError(null);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async () => {
    if (!readAccessToken()) {
      discardSession();
      return;
    }
    setStatus('loading');
    try {
      await adoptSession();
    } catch (cause) {
      // Only an explicit rejection ends the session. A transport failure must
      // not sign the user out — the token may still be perfectly valid.
      if (isUnauthorized(cause)) {
        discardSession();
        return;
      }
      setUser(null);
      setError(cause instanceof Error ? cause.message : PROFILE_UNAVAILABLE);
      setStatus('error');
    }
  }, [adoptSession, discardSession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const token = await requestAccessToken(email, password);
      storeAccessToken(token);
      try {
        await adoptSession();
      } catch {
        clearAccessToken();
        setUser(null);
        setStatus('anonymous');
        throw new Error(PROFILE_UNAVAILABLE);
      }
    },
    [adoptSession],
  );

  // Resolve the stored session once per mounted provider. The ref keeps
  // StrictMode's deliberate double-invoke from issuing two identity requests.
  const resolvedOnMount = useRef(false);
  useEffect(() => {
    if (resolvedOnMount.current) return;
    resolvedOnMount.current = true;
    if (!readAccessToken()) return;
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, error, signIn, signOut: discardSession, refresh }),
    [user, status, error, signIn, discardSession, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth() требует, чтобы дерево было обёрнуто в <AuthProvider>.');
  }
  return context;
}

export type { CurrentUser };
